/**
 * Feature Flags Store
 *
 * Pinia store for managing global feature flags with in-memory caching.
 * Both console and frontend apps use this store.
 *
 * Features:
 * - In-memory cache (Map) for fast flag lookups
 * - Auto-invalidates cache on mutations
 * - Loading states for async operations
 * - Error handling
 * - Uses dependency injection for FeatureFlagsService
 *
 * Caching strategy:
 * - Fetch flags once on app load
 * - Cache in Map<string, boolean> for O(1) lookups
 * - Optional: Manual refresh for admins
 * - Cache cleared on page reload
 */

import {defineStore} from 'pinia'
import {computed, inject, ref} from 'vue'
import type {FeatureFlagsService} from './service'
import type {FeatureFlagDetail, UpdateFeatureFlagRequest} from './types'
import {mapFeatureFlagDetailFromResponse} from './types'

/**
 * Injection key for FeatureFlagsService
 * Apps must provide this in main.ts:
 * app.provide(FEATURE_FLAGS_SERVICE_KEY, new FeatureFlagsService(axiosInstance))
 */
export const FEATURE_FLAGS_SERVICE_KEY = Symbol('featureFlagsService') as symbol

/**
 * Feature Flags Pinia Store
 *
 * Manages global feature flags state with in-memory caching.
 * Provides reactive access to feature flags across the application.
 *
 * MONOREPO REQUIREMENT:
 * Consuming apps MUST add to vite.config.ts:
 *   resolve: { dedupe: ['pinia', 'vue'] }
 * This prevents duplicate Pinia instances that cause production errors.
 *
 * @example
 * // In component
 * const store = useFeatureFlagsStore()
 * await store.fetchFlags()
 * const isEnabled = store.isEnabled('communication_v2_enabled')
 */
export const useFeatureFlagsStore = defineStore('featureFlags', () => {
  // =============================================================================
  // DEPENDENCY INJECTION
  // =============================================================================

  // Service injected from app context during store setup (must be synchronous)
  // Works because vite.config.ts dedupe ensures single Pinia instance
  const _service = inject<FeatureFlagsService | null>(FEATURE_FLAGS_SERVICE_KEY, null)

  function getService(): FeatureFlagsService {
    if (!_service) {
      throw new Error(
        'FeatureFlagsService not provided. Ensure app.provide(FEATURE_FLAGS_SERVICE_KEY, service) is called in main.ts'
      )
    }
    return _service
  }

  // =============================================================================
  // STATE
  // =============================================================================

  /**
   * In-memory cache of feature flags (key -> enabled state)
   * Uses Map for O(1) lookups
   */
  const flags = ref(new Map<string, boolean>())

  /**
   * Detailed feature flags (key -> full metadata)
   * Only populated when fetchDetailed() is called (admin UI)
   */
  const detailedFlags = ref(new Map<string, FeatureFlagDetail>())

  /**
   * Loading state for async operations
   */
  const loading = ref(false)

  /**
   * Timestamp of last successful fetch
   * Used to determine cache freshness
   */
  const lastFetched = ref<Date | null>(null)

  /**
   * Error message from last failed operation
   */
  const error = ref<string | null>(null)

  // =============================================================================
  // GETTERS
  // =============================================================================

  /**
   * Check if a feature flag is enabled
   *
   * Returns false if flag not found (safe default for feature gating)
   *
   * @param key - Feature flag key
   * @returns True if flag is enabled, false otherwise
   */
  const isEnabled = computed(() => {
    return (key: string): boolean => {
      return flags.value.get(key) ?? false
    }
  })

  /**
   * Get detailed feature flag by key
   *
   * Returns null if flag not found or detailed data not loaded
   *
   * @param key - Feature flag key
   * @returns Detailed feature flag or null
   */
  const getDetailedFlag = computed(() => {
    return (key: string): FeatureFlagDetail | null => {
      return detailedFlags.value.get(key) ?? null
    }
  })

  /**
   * Get all detailed flags as array
   * Used in admin UI for displaying all flags
   */
  const allDetailedFlags = computed(() => {
    return Array.from(detailedFlags.value.values())
  })

  /**
   * Check if cache is fresh (has been fetched)
   */
  const isCached = computed(() => {
    return lastFetched.value !== null
  })

  // =============================================================================
  // ACTIONS - READ OPERATIONS (both console and frontend)
  // =============================================================================

  /**
   * Fetch all feature flags (simple key-value map)
   *
   * Fetches lightweight flag data from GET /features.
   * Use this on app load for fast flag checks.
   *
   * @param forceRefresh - Skip cache and fetch fresh data
   * @throws Service error if request fails
   *
   * @example
   * await store.fetchFlags()
   * // Cache populated, ready for isEnabled() checks
   */
  async function fetchFlags(forceRefresh = false): Promise<void> {
    // Skip fetch if already cached and not forcing refresh
    if (!forceRefresh && isCached.value) {
      return
    }

    loading.value = true
    error.value = null

    try {
      const flagsMap = await getService().getAllFlags()

      // Update cache
      flags.value = new Map(Object.entries(flagsMap))
      lastFetched.value = new Date()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch feature flags'
      error.value = errorMessage
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch all feature flags with full details
   *
   * Fetches detailed flag data from GET /features/detailed.
   * Use this in admin UI when you need to display/edit flag metadata.
   *
   * @param forceRefresh - Skip cache and fetch fresh data
   * @throws Service error if request fails
   *
   * @example
   * await store.fetchDetailed()
   * const flags = store.allDetailedFlags
   * // Display in admin UI table
   */
  async function fetchDetailed(forceRefresh = false): Promise<void> {
    // Skip fetch if already cached and not forcing refresh
    if (!forceRefresh && detailedFlags.value.size > 0) {
      return
    }

    loading.value = true
    error.value = null

    try {
      const detailed = await getService().getAllDetailed()

      // Convert API responses to domain model (string dates → Date objects)
      const domainModels = detailed.map(mapFeatureFlagDetailFromResponse)

      // Update detailed cache
      detailedFlags.value = new Map(domainModels.map((f) => [f.key, f]))

      // Also update simple flags map for isEnabled() checks
      flags.value = new Map(domainModels.map((f) => [f.key, f.enabled]))

      lastFetched.value = new Date()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch detailed feature flags'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Check if a specific flag is enabled (async, fetches if not cached)
   *
   * Useful for on-demand checks of individual flags.
   * Prefer fetchFlags() + isEnabled() for bulk checks.
   *
   * @param key - Feature flag key
   * @returns True if flag is enabled, false otherwise
   * @throws Service error if request fails
   *
   * @example
   * const enabled = await store.checkFlag('communication_v2_enabled')
   */
  async function checkFlag(key: string): Promise<boolean> {
    // Return cached value if available
    if (flags.value.has(key)) {
      return isEnabled.value(key)
    }

    // Fetch individual flag
    try {
      const enabled = await getService().getFlag(key)

      // Update cache
      flags.value.set(key, enabled)

      return enabled
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to check flag: ${key}`
      error.value = errorMessage
      throw err
    }
  }

  // =============================================================================
  // ACTIONS - ADMIN OPERATIONS (console only, tree-shaken in frontend)
  // =============================================================================

  /**
   * Update a feature flag (admin only)
   *
   * Updates flag state on backend and invalidates cache.
   * Requires admin authentication.
   *
   * Tree-shaking: This method is only included in console bundle.
   *
   * @param key - Feature flag key
   * @param request - Update request with enabled state, description, expiration
   * @throws Service error if unauthorized or request fails
   *
   * @example
   * await store.updateFlag('communication_v2_enabled', {
   *   enabled: true,
   *   description: 'Enable new communication system',
   *   expiresAt: '2025-12-31T23:59:59Z',
   *   rolloutPercentage: 100
   * })
   */
  async function updateFlag(key: string, request: UpdateFeatureFlagRequest): Promise<FeatureFlagDetail> {
    loading.value = true
    error.value = null

    try {
      const updated = await getService().updateFlag(key, request)

      // Convert API response to domain model
      const domainModel = mapFeatureFlagDetailFromResponse(updated)

      // Update cache with fresh data
      flags.value.set(key, domainModel.enabled)
      detailedFlags.value.set(key, domainModel)

      return domainModel
    } catch (err) {
      error.value = err instanceof Error ? err.message : `Failed to update flag: ${key}`
      throw err
    } finally {
      loading.value = false
    }
  }

  // =============================================================================
  // CACHE MANAGEMENT
  // =============================================================================

  /**
   * Clear all cached flags
   *
   * Use this when logging out or when you want to force a fresh fetch.
   */
  function clearCache(): void {
    flags.value.clear()
    detailedFlags.value.clear()
    lastFetched.value = null
    error.value = null
  }

  // =============================================================================
  // RETURN PUBLIC API
  // =============================================================================

  return {
    // State (for debugging/inspection)
    flags,
    detailedFlags,
    loading,
    lastFetched,
    error,

    // Getters
    isEnabled,
    getDetailedFlag,
    allDetailedFlags,
    isCached,

    // Read operations
    fetchFlags,
    fetchDetailed,
    checkFlag,

    // Admin operations (tree-shaken in frontend)
    updateFlag,

    // Cache management
    clearCache
  }
})
